import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { debounce } from "lodash";
import MainBody from "../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../components/Pagination";
import AddEditOpeningBalance from "./AddEditOpeningBalance";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import { Edit, LucidePlusCircle } from "lucide-react";
import OpeningBalanceActionButton from "./OpeningBalanceActionButton";
import Confirmation from "../../components/Confirmation";
import { useTrust } from "../../lib/context/TrustContext";
import {
  formatNumber,
  formatSubcontractorName,
  getModuleNamingBySlug,
} from "../../lib/Misc";
import { useOrganization } from "../../lib/context/OrganisationContext";
import AddEditTrust from "../Trust/AddEditTrust";
import moment from "moment";

const OpeningBalances = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { trustId, trustData } = useTrust();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [openingBalanceList, setOpeningBalancesList] = useState([]);
  const [openingBalanceData, setOpeningBalancesData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const [openingBalanceId, setOpeningBalanceId] = useState(null);
  const [showAddEditOpeningBalance, setShowAddEditOpeningBalance] =
    useState(false);
  const [showOpeningBalanceDelete, setShowOpeningBalanceDelete] =
    useState(false);
  const [showAddEditTrust, setShowAddEditTrust] = React.useState(false);

  const { organizationData } = useOrganization();

  const toggleShowAddEditOpeningBalance = () => {
    if (showAddEditOpeningBalance) {
      setEdit(false);
      setOpeningBalanceId(null);
    }
    setShowAddEditOpeningBalance(!showAddEditOpeningBalance);
  };

  const toggleShowDelete = () => {
    if (showOpeningBalanceDelete) {
      setOpeningBalanceId(null);
    }
    setShowOpeningBalanceDelete(!showOpeningBalanceDelete);
  };

  const toggleShowAddEditTrust = () => {
    setShowAddEditTrust(!showAddEditTrust);
  };

  const fetchOpeningBalances = useCallback(
    async (passLimit, passPage) => {
      if (trustId) {
        try {
          setLoading(true);
          const { data } = await get(
            `${API.GET_OPENING_BALANCES}?limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&search=${
              searchParam ? searchParam : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }&trust_id=${trustId ? trustId : ""}`,
          );

          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          const openingBalanceList = data.data.data;
          let notSelectedIds = [];
          for (let i = 0; i < openingBalanceList?.length; i++) {
            notSelectedIds.push(openingBalanceList[i]?.id);
          }
          setOpeningBalancesData(data?.opening_balance_data);
          // setNotSelectedRowIds(notSelectedIds);
          setOpeningBalancesList(openingBalanceList);
          return data.data;
        } catch (error) {
          setLoading(false);
          setOpeningBalancesList([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, trustId],
  );

  const openingBalancesGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: openingBalanceList[i]?.id,
        id: openingBalanceList[i]?.id,
        transaction_start_date: openingBalanceList[i]?.transaction_start_date
          ? moment(
              openingBalanceList[i]?.transaction_start_date,
              "DD-MM-YYYY",
            ).format("DD-MM-YYYY")
          : "-",
        name: openingBalanceList[i]?.contactable
          ? formatSubcontractorName(openingBalanceList[i]?.contactable)
          : "-",
        contactable_type: openingBalanceList[i]?.contactable_type
          ? openingBalanceList[i]?.contactable_type === "vendor"
            ? getModuleNamingBySlug(
                organizationData?.organizationData,
                "vendors",
                "Vendor",
              )
            : openingBalanceList[i]?.contactable_type === "contact"
              ? getModuleNamingBySlug(
                  organizationData?.organizationData,
                  "contacts",
                  "Customer",
                )
              : ""
          : "",
        opening_balance: openingBalanceList[i]?.opening_balance
          ? `$${formatNumber(Number(openingBalanceList[i]?.opening_balance))}`
          : "$0.00",
        remaining_balance: openingBalanceList[i]?.remaining_balance
          ? `$${formatNumber(Number(openingBalanceList[i]?.remaining_balance))}`
          : "$0.00",
        action: openingBalanceList[i]?.is_used ? (
          <></>
        ) : (
          <>
            <OpeningBalanceActionButton
              id={openingBalanceList[i]?.id}
              setEdit={setEdit}
              setOpeningBalanceId={setOpeningBalanceId}
              toggleShowAddEditOpeningBalance={toggleShowAddEditOpeningBalance}
              toggleShowDelete={toggleShowDelete}
              used={openingBalanceList[i]?.is_used ? true : false}
            />
          </>
        ),
      });
    }
    return items;
  };

  const openingBalances = openingBalancesGenerator(openingBalanceList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "transaction_start_date",
      text: "Transaction Start Date",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Name",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "contactable_type",
      text: "Type",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "opening_balance",
      text: "Opening Balance",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "remaining_balance",
      text: "Remaining Balance",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC",
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      },
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchOpeningBalances();
  }, [fetchOpeningBalances]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    [],
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchOpeningBalances(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchOpeningBalances(limit, page);
  };

  // Single COA Delete
  const handleOpeningBalanceDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.OPENING_BALANCE_DELETE}/${openingBalanceId}`);
      setLoading(false);
      fetchOpeningBalances();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        search={false}
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        mainTitle={"Opening Balances"}
        actionContentDirect={true}
        actionAlign={"start"}
        actionContent={
          <div className="w-full">
            <div
              className={`w-full flex-wrap flex items-center justify-between border rounded-xl p-3 py-5 bg-muted/50 ${
                Number(openingBalanceData?.trust_balance) ===
                Number(openingBalanceData?.total)
                  ? "border-green-500"
                  : "border-red-500"
              }`}
            >
              <div>
                {trustData &&
                  (trustData?.business_name
                    ? trustData?.business_name
                    : trustData?.first_name
                      ? trustData?.first_name + " " + trustData?.last_name
                      : trustData?.last_name)}
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center flex-col">
                  <p>Trust Opening Balance</p>
                  <div className="font-bold text-xl">
                    $
                    {openingBalanceData?.trust_balance
                      ? formatNumber(Number(openingBalanceData?.trust_balance))
                      : "0.00"}
                    <span>
                      <ResponsiveDrawerForm
                        description={""}
                        title={"Edit Trust"}
                        handleOpen={() => {}}
                        handleClose={() => {
                          setEdit(false);
                          setShowAddEditTrust(false);
                        }}
                        buttonText={
                          <div className="flex items-center gap-1">
                            <Edit size={16} />
                          </div>
                        }
                        className={""}
                        open={showAddEditTrust}
                        buttonClassName={"p-0 ml-2 h-fit p-1"}
                        setOpen={setShowAddEditTrust}
                        write={true}
                      >
                        <AddEditTrust
                          edit={true}
                          orgId={organizationData?.organizationId}
                          trustID={trustId}
                          tab={true}
                          toggleShowAddEditTrust={toggleShowAddEditTrust}
                          fetchTrusts={fetchOpeningBalances}
                        />
                      </ResponsiveDrawerForm>
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-center flex-col">
                  <p>All Contact's Cumulative Total</p>
                  <div className="font-bold text-xl">
                    $
                    {openingBalanceData?.total
                      ? formatNumber(Number(openingBalanceData?.total))
                      : "0.00"}
                  </div>
                </div>
                <div className="flex items-center justify-center flex-col">
                  <p>Difference(Yet To Enter)</p>
                  <div className="font-bold text-xl">
                    {openingBalanceData?.trust_balance ||
                    openingBalanceData?.total
                      ? Number(openingBalanceData?.trust_balance) <
                        Number(openingBalanceData?.total)
                        ? `($${formatNumber(
                            Number(
                              Number(
                                Math.abs(
                                  Number(
                                    openingBalanceData?.trust_balance?.toFixed(
                                      2,
                                    ),
                                  ) -
                                    Number(
                                      openingBalanceData?.total?.toFixed(2),
                                    ),
                                ),
                              ),
                            ),
                          )})`
                        : `$${formatNumber(
                            Number(
                              Number(
                                openingBalanceData?.trust_balance?.toFixed(2),
                              ) - Number(openingBalanceData?.total?.toFixed(2)),
                            ),
                          )}`
                      : "$0.00"}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-end mt-4">
              {/* Add OpeningBalance */}
              <ResponsiveDrawerForm
                description={""}
                title={edit ? "Edit Opening Balance" : "Add Opening Balance"}
                handleOpen={() => {}}
                handleClose={() => {
                  setEdit(false);
                  setOpeningBalanceId(null);
                  setShowAddEditOpeningBalance(false);
                }}
                buttonText={
                  <div className="flex items-center gap-1">
                    <LucidePlusCircle size={18} />
                    <span>Add Opening Balance</span>
                  </div>
                }
                className={"sm:max-w-1/2"}
                open={showAddEditOpeningBalance}
                buttonClassName={""}
                setOpen={setShowAddEditOpeningBalance}
                write={true}
              >
                <AddEditOpeningBalance
                  edit={edit}
                  toggleShowAddEditOpeningBalance={
                    toggleShowAddEditOpeningBalance
                  }
                  id={openingBalanceId}
                  trustId={trustId}
                  fetchOpeningBalanceData={() =>
                    fetchOpeningBalances(limit, page)
                  }
                />
              </ResponsiveDrawerForm>
            </div>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class max-h-[600px]">
            <BootstrapTable
              keyField="key"
              remote
              data={openingBalances}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Opening Balance"}
        open={showOpeningBalanceDelete}
        setOpen={setShowOpeningBalanceDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete opening balance?"}
        okAction={async () => {
          handleOpeningBalanceDelete();
          toggleShowDelete();
          fetchOpeningBalances();
        }}
        cancelAction={() => {
          setOpeningBalanceId(null);
          setShowOpeningBalanceDelete(!showOpeningBalanceDelete);
        }}
      />
    </>
  );
};

export default OpeningBalances;
